import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetContractFeeScheduleQuery,
  usePreviewFeeScheduleMutation,
  IRateCalculationResult,
  normalizeRateType,
} from "store/queries/contractFeeSchedules";

export function ContractPreviewPage() {
  const { id, contractId } = useParams<{ id: string; contractId?: string }>();
  const navigate = useNavigate();

  // In hospital context, use contractId; in admin context, use id
  const actualContractId = contractId || id;
  const hospitalId = contractId ? id : null; // If contractId exists, then id is the hospital ID
  useDocumentTitle("Contract Rate Preview");
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);

  const {
    data: contractData,
    isLoading: isLoadingContract,
    error: contractError,
  } = useGetContractFeeScheduleQuery(actualContractId!);

  const [previewFeeSchedule, { isLoading: isLoadingPreview }] =
    usePreviewFeeScheduleMutation();

  const contract = contractData?.data;

  // Preview state
  const [previewData, setPreviewData] = useState<{
    rates: IRateCalculationResult[];
    total: number;
    hasMore: boolean;
    totalCarveouts: number;
    cacheStats?: any;
  } | null>(null);

  // Filter state
  const [cptCodeSearch, setCptCodeSearch] = useState("");
  const [isCarveoutOnly, setIsCarveoutOnly] = useState(false);

  useEffect(() => {
    setIsLoading(isLoadingContract || isLoadingPreview);
  }, [isLoadingContract, isLoadingPreview, setIsLoading]);

  useEffect(() => {
    if (contractError) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load contract details.",
      });
    }
  }, [contractError, toastElement]);

  const loadPreview = async (options?: {
    cptCodes?: string[];
    carveoutOnly?: boolean;
  }) => {
    if (!contract) return;

    try {
      const result = await previewFeeSchedule({
        contractId: actualContractId!,
        params: {
          cptCodes: options?.cptCodes,
          carveoutOnly: options?.carveoutOnly,
        },
      }).unwrap();

      setPreviewData(result);
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Preview Failed",
        detail: "Failed to load rate preview.",
      });
    }
  };

  useEffect(() => {
    // Load preview when contract is loaded
    if (contract) {
      loadPreview();
    }
  }, [contract]);

  const handleSearch = () => {
    const cptCodes = cptCodeSearch
      .split(",")
      .map((code) => code.trim())
      .filter((code) => code.length > 0);

    loadPreview({
      cptCodes: cptCodes.length > 0 ? cptCodes : undefined,
      carveoutOnly: isCarveoutOnly,
    });
  };

  if (!contract) {
    return (
      <div className="card">
        <div className="text-center p-4">
          {isLoadingContract
            ? "Loading contract details..."
            : "Contract not found"}
        </div>
      </div>
    );
  }

  // Filter displayed rates based on selected filters
  const filteredRates =
    previewData?.rates?.filter((rate) => {
      if (isCarveoutOnly && !rate.isCarveout) {
        return false;
      }
      return true;
    }) || [];

  const calculatedRateTemplate = (rowData: IRateCalculationResult) => {
    return (
      <div className="flex align-items-center gap-2">
        <span className="font-semibold">
          ${rowData.calculatedRate.toFixed(2)}
        </span>
        {rowData.isCarveout && (
          <i
            className="pi pi-exclamation-circle text-orange-500"
            title="Carveout rate applied"
          />
        )}
      </div>
    );
  };

  const sourceTemplate = (rowData: IRateCalculationResult) => {
    if (rowData.isCarveout) {
      return (
        <div className="flex align-items-center gap-1">
          <i className="pi pi-file text-orange-500" />
          <span className="text-orange-600 font-medium">Carveout</span>
        </div>
      );
    }
    return <span className="text-600">{rowData.source || "Base Rate"}</span>;
  };

  const rateDetailsTemplate = (rowData: IRateCalculationResult) => {
    const rateType = normalizeRateType(rowData.rateType, "percent");
    return (
      <div className="text-sm">
        <div>Base: ${rowData.baseRate.toFixed(2)}</div>
        <div>
          {rateType === "unit"
            ? `Price per Unit: ${rowData.ratePercent / 100}`
            : `Rate %: ${rowData.ratePercent}%`}
        </div>
        {rowData.isCarveout && rowData.carveoutRate && (
          <div className="text-orange-600">
            Override: ${rowData.carveoutRate.toFixed(2)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="mb-3">
        <Button
          label="Return to Contract"
          icon="pi pi-arrow-left"
          className="p-button-text p-button-plain text-600"
          onClick={() => {
            const contractUrl = hospitalId
              ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}`
              : `/payer-rules/contracts/${actualContractId}`;
            navigate(contractUrl);
          }}
        />
      </div>
      <div className="mb-4">
        <h2 className="m-0 mb-2">Rate Preview: {contract.name}</h2>
        <div className="flex align-items-center gap-2">
          <span className="text-600">
            {contract.provider?.name} - {contract.payer?.name}
          </span>
        </div>
      </div>

      {/* Search and Filter Section */}
      <Card className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-4">
            <label className="block mb-2">Search CPT Codes</label>
            <div className="p-inputgroup">
              <InputText
                value={cptCodeSearch}
                onChange={(e) => setCptCodeSearch(e.target.value)}
                placeholder="Enter CPT codes (comma-separated)"
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button icon="pi pi-search" onClick={handleSearch} />
            </div>
          </div>
          <div className="col-12 md:col-8 flex align-items-end">
            <div className="field-checkbox mb-0">
              <input
                type="checkbox"
                id="carveoutOnly"
                checked={isCarveoutOnly}
                onChange={(e) => {
                  setIsCarveoutOnly(e.target.checked);
                  loadPreview({
                    carveoutOnly: e.target.checked,
                  });
                }}
              />
              <label htmlFor="carveoutOnly" className="ml-2">
                Carveout Rates Only
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Section */}
      {previewData && (
        <div className="grid mb-4">
          <div className="col-12 md:col-4">
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {previewData.total}
                </div>
                <div className="text-600">Total Rates</div>
              </div>
            </Card>
          </div>
          <div className="col-12 md:col-4">
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-500">
                  {previewData.totalCarveouts || 0}
                </div>
                <div className="text-600">Carveout Rates</div>
              </div>
            </Card>
          </div>
          <div className="col-12 md:col-4">
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">
                  {filteredRates.length}
                </div>
                <div className="text-600">Displayed Rates</div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Rates Table */}
      <Card title="Calculated Rates">
        {filteredRates.length > 0 ? (
          <>
            {previewData?.hasMore && (
              <Message
                severity="info"
                text={`Showing first ${filteredRates.length} rates. Use search to find specific CPT codes.`}
                className="mb-3"
              />
            )}
            <DataTable
              value={filteredRates}
              paginator
              rows={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              responsiveLayout="scroll"
              className="p-datatable-sm"
              stripedRows
            >
              <Column
                field="cptCode"
                header="CPT Code"
                sortable
                style={{ width: "10%" }}
              />
              <Column
                field="description"
                header="Description"
                sortable
                style={{ width: "30%" }}
              />
              <Column
                header="Calculated Rate"
                body={calculatedRateTemplate}
                sortable
                field="calculatedRate"
                style={{ width: "15%" }}
              />
              <Column
                header="Rate Details"
                body={rateDetailsTemplate}
                style={{ width: "18%" }}
              />
              <Column
                header="Source"
                body={sourceTemplate}
                style={{ width: "15%" }}
              />
            </DataTable>
          </>
        ) : (
          <div className="text-center p-4 text-600">
            {isLoadingPreview
              ? "Loading rates..."
              : "No rates found. Try adjusting your search criteria."}
          </div>
        )}
      </Card>
    </div>
  );
}

export default ContractPreviewPage;
